import type { ReservationMode } from '@iheartjane/types';
import { sentenceCase } from '@iheartjane/utils';
import { useMemo } from 'react';
import type { DeepReadonly } from 'ts-essentials';

import type { StoreSchedule } from '@jane/shared-ecomm/util';
// eslint-disable-next-line no-restricted-imports
import type { Store } from '@jane/shared/models';
import type { Store as zStore } from '@jane/shared/types';

export interface UseCartReservationAlertProps {
  curbsideLabel?: string;
  curbsideSchedule?: StoreSchedule | null;
  deliveryLabel: string;
  deliverySchedule: StoreSchedule | null;
  hasCurbsideSlots?: boolean;
  hasDeliverySlots: boolean;
  hasPickupSlots: boolean;
  pickupLabel: string;
  pickupSchedule: StoreSchedule | null;
  reservationMode: ReservationMode;
  store: DeepReadonly<Store> | zStore;
}

export const useCartReservationAlert = ({
  curbsideLabel,
  curbsideSchedule,
  deliveryLabel,
  deliverySchedule,
  hasCurbsideSlots,
  hasDeliverySlots,
  hasPickupSlots,
  pickupLabel,
  pickupSchedule,
  reservationMode,
  store,
}: UseCartReservationAlertProps): string | null => {
  const modeLabel =
    reservationMode === 'delivery'
      ? deliveryLabel
      : reservationMode === 'curbside'
        ? (curbsideLabel ?? pickupLabel)
        : pickupLabel;
  const defaultAlert = `You can't proceed to checkout because ${store.name} is closed`;
  const reservationModeMessage = `${store.name} online ordering for ${modeLabel} is`;
  const noTimeSlotsMessage = `${modeLabel} is currently unavailable`;

  return useMemo(() => {
    if (!deliverySchedule && !pickupSchedule) {
      return null;
    }

    // if no mode and both schedules are closed, default message
    if (
      !reservationMode &&
      deliverySchedule?.isCurrentlyClosed() &&
      pickupSchedule?.isCurrentlyClosed()
    ) {
      return defaultAlert;
    }

    // if closed and does not allow off hours ordering
    if (reservationMode === 'pickup') {
      if (
        pickupSchedule?.isCurrentlyClosed() &&
        !store?.allow_off_hours_ordering
      ) {
        // if open later today
        if (pickupSchedule?.willOpenLaterToday()) {
          return `${reservationModeMessage} closed until ${
            pickupSchedule?.getToday().formattedOpeningTime
          } today`;
        }

        // if next open day
        if (pickupSchedule?.getNextOpenDay()?.dayName) {
          return `${reservationModeMessage} closed until ${
            pickupSchedule.getNextOpenDay()!.formattedOpeningTime
          } on ${sentenceCase(pickupSchedule.getNextOpenDay()!.dayName)}`;
        }

        return defaultAlert;
      }

      // open but no slots available
      if (!hasPickupSlots) {
        return noTimeSlotsMessage;
      }
    }

    if (reservationMode === 'curbside') {
      if (
        curbsideSchedule?.isCurrentlyClosed() &&
        !store?.allow_off_hours_ordering
      ) {
        if (curbsideSchedule?.willOpenLaterToday()) {
          return `${reservationModeMessage} closed until ${
            curbsideSchedule?.getToday().formattedOpeningTime
          } today`;
        }

        if (curbsideSchedule?.getNextOpenDay()?.dayName) {
          return `${reservationModeMessage} closed until ${
            curbsideSchedule.getNextOpenDay()!.formattedOpeningTime
          } on ${sentenceCase(curbsideSchedule.getNextOpenDay()!.dayName)}`;
        }

        return defaultAlert;
      }

      // open but no slots available
      if (!hasCurbsideSlots) {
        return noTimeSlotsMessage;
      }
    }

    if (reservationMode === 'delivery') {
      // if closed and does not allow off hours ordering
      if (
        deliverySchedule?.isCurrentlyClosed() &&
        !store?.allow_off_hours_ordering
      ) {
        // if open later today
        if (deliverySchedule?.willOpenLaterToday()) {
          return `${reservationModeMessage} closed until ${
            deliverySchedule?.getToday().formattedOpeningTime
          } today`;
        }

        // if next open day
        if (deliverySchedule?.getNextOpenDay()?.dayName) {
          return `${reservationModeMessage} closed until ${
            deliverySchedule.getNextOpenDay()!.formattedOpeningTime
          } on ${sentenceCase(deliverySchedule.getNextOpenDay()!.dayName)}`;
        }

        return defaultAlert;
      }

      // open but no slots available
      if (!hasDeliverySlots) {
        return noTimeSlotsMessage;
      }
    }

    return null;
  }, [
    curbsideSchedule,
    defaultAlert,
    deliverySchedule,
    hasCurbsideSlots,
    hasDeliverySlots,
    hasPickupSlots,
    noTimeSlotsMessage,
    pickupSchedule,
    reservationMode,
    reservationModeMessage,
    store?.allow_off_hours_ordering,
  ]);
};
